import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DeleteModal, ExpandableTable, Typography } from '@thinknet-gmbh/platform-common-ui';
import {
  DatevNumberStatusesEnum,
  PERMISSION_ACTIONS,
  PLATFORM_PERMISSIONS,
} from '@thinknet-gmbh/platform-common';

import { usePermissionCheck } from 'src/hooks/permission-check.hook';
import { useDeleteDatevNumberById } from 'src/hooks/datev-numbers/delete-datev-number-by-id.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetConsultantNumbers } from 'src/hooks/consultant-numbers/get-consultant-numbers.hook';

import PermissionCheck from 'src/components/permission-check';
import { CreateDatevNumber } from './components/create-datev-number';
import { DatevListTable } from './components/datev-list-table';
import { ChangeDatevNumber } from './components/change-datev-number';
import { DatevSetting } from './components/datev-setting';

import { ROUTES } from 'src/components/app/app-routes.constants';
import { SETTINGS_PAGES } from 'src/pages/settings/settings.constants';
import { DATEV_MODALS, DEFAULT_DATEV_TO_DELETE } from './datev.constants';
import { DatevWrapper, ExpandableStyledTable, SettingContainer } from './datev.styles';

const Datev = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('settings');
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [isOpenedAttention, setIsOpenedAttention] = useState(false);
  const [datevToDelete, setDatevToDelete] = useState(DEFAULT_DATEV_TO_DELETE);

  const currentCompanyId = useGetCurrentCompanyId();

  const { collection, paginationConfig, onTableChange } = useGetConsultantNumbers(currentCompanyId);
  const { checkRule } = usePermissionCheck(PLATFORM_PERMISSIONS.datevManagement);

  const closeModal = () => {
    setActiveModal(null);
    setIsOpenedAttention(false);
    setDatevToDelete(DEFAULT_DATEV_TO_DELETE);
  };

  const deleteDatevNumberById = useDeleteDatevNumberById({ onSuccess: closeModal });

  const deleteDatevNumber = () => {
    deleteDatevNumberById({ variables: { datevNumberId: datevToDelete._id } });
    setDatevToDelete(DEFAULT_DATEV_TO_DELETE);
    closeModal();
  };

  const onSubmitDelete = () => {
    if (datevToDelete.status === DatevNumberStatusesEnum.FREE) {
      deleteDatevNumber();
    } else {
      setActiveModal(DATEV_MODALS.change);
    }
  };

  const onSubmitChangeDatevNumber = () => {
    deleteDatevNumber();
  };

  const closeAttempt = (forceClosing?: boolean) => {
    if (isOpenedAttention || forceClosing) closeModal();

    setIsOpenedAttention(!isOpenedAttention);
  };

  return (
    <DatevWrapper>
      {activeModal === DATEV_MODALS.change && (
        <ChangeDatevNumber
          isOpenedAttention={isOpenedAttention}
          isOpened
          datev={datevToDelete}
          onFinish={onSubmitChangeDatevNumber}
          onClose={closeAttempt}
        />
      )}
      <CreateDatevNumber
        isOpened={activeModal === DATEV_MODALS.create}
        onClose={closeModal}
        consultantNumberId={datevToDelete.consultantNumberId}
      />
      <DeleteModal
        translate={t}
        isOpened={activeModal === DATEV_MODALS.delete}
        title={t('datev.deleteModal.title')}
        subTitle={t('datev.deleteModal.subtitle', { clientNumber: datevToDelete.datevNumber })}
        onClose={closeModal}
        onSubmit={onSubmitDelete}
      />
      <SettingContainer>
        <PermissionCheck
          permission={PLATFORM_PERMISSIONS.datevManagement}
          action={PERMISSION_ACTIONS.create}
        >
          <DatevSetting
            currentCompanyId={currentCompanyId}
            handleCreate={() => setActiveModal(DATEV_MODALS.create)}
          />
        </PermissionCheck>
      </SettingContainer>
      <ExpandableStyledTable
        t={t}
        dataSource={collection}
        paginationConfig={paginationConfig}
        onChange={onTableChange}
        isRowClicked
        onRow={record => ({
          onClick: () => {
            const canEdit = checkRule(
              PERMISSION_ACTIONS.update,
              accountId => accountId === record.createdBy
            );

            if (canEdit) {
              navigate(
                `${ROUTES.settings}?type=${SETTINGS_PAGES.editConsultantNumber}&id=${record._id}`
              );
            }
          },
        })}
        expandedRowRender={({ datevClientNumbers, _id }: any) => (
          <DatevListTable
            dataSource={datevClientNumbers}
            consultantNumberId={_id}
            handleModal={(modal, record) => {
              setActiveModal(modal);
              setDatevToDelete(record);
            }}
          />
        )}
      >
        <ExpandableTable.Column
          title={t('datev.labels.consultantNumber')}
          dataIndex="consultantNumber"
          key="consultantNumber"
          sorter
          width={250}
          render={number => <Typography>{number}</Typography>}
        />
        <ExpandableTable.Column
          title={t('employee:labels.details')}
          key="details"
          dataIndex="details"
          render={details => <Typography>{details}</Typography>}
        />
      </ExpandableStyledTable>
    </DatevWrapper>
  );
};

export default Datev;
